// The chat's knowledge graph in the footer, sized and behaved like
// its neighbours: a 34px icon with a one-line hover label. Clicking opens
// a panel that stays until it is dismissed, so the investigation inside
// (GraphTimeline) can be stepped through and read; the detail per call
// lives in the transcript.
import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import GraphTimeline from "./GraphTimeline.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { graphKeyFor, graphSummary, graphVersion, notifyGraphChanged, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { pluralKind } from "../lib/investigationGraphView.js";
import { openChatCanvas } from "../lib/traceCanvasStore.js";
import { claimTraceSession, graphTraceSessions, parseGraphFocus } from "../lib/traceGraphOverlay.js";
import { chatSessionsToLay } from "../lib/canvasFollow.js";

/**
 * The traces a chat's workspace lays on: every one its claims rest on,
 * directly or through one of their events (lib/canvasFollow.js
 * chatSessionsToLay), the one most of them rest on first. Never a trace a
 * search or an endpoint's timeline only named: before any claim, none.
 */
export function workspaceTraces(graph) {
    const claimed = new Set(chatSessionsToLay(graph).map((s) => s.id));
    return graphTraceSessions(graph).filter((t) => claimed.has(String(t.id).toLowerCase()));
}

// The investigation on one canvas over the page: the traces its claims rest
// on (workspaceTraces), anchored on the one most of its claims rest on,
// worked in this chat; `focus` brings one of them (a
// claim's) into view, laid on whatever rests on it. The rest stay in the
// graph's list of traces and the sidebar's Chat list.
export function openGraphCanvas({ graph, graphKey, title = "", focus = null }) {
    if (!graph || !parseGraphFocus(graphKey)) return false;
    const chosen = workspaceTraces(graph);
    const asked = focus ? graphTraceSessions(graph).find((t) => String(t.id).toLowerCase() === String(focus).toLowerCase()) : null;
    const traces = [...chosen, ...(asked && !chosen.some((t) => t.id === asked.id) ? [asked] : [])].map((t) => ({ id: t.id, title: t.label || "" }));
    return !!openChatCanvas({ graphKey, title, traces, focus });
}

const ROW = "flex items-start gap-[8px] border-b border-[color:var(--line-soft)] py-[3px] text-[11px] leading-[1.35] last:border-b-0";
const LINK = "shrink-0 cursor-pointer bg-transparent p-0 text-[10.5px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";

// The traces the graph names, and one door to all of them: the
// investigation's canvas, once a claim rests on one (`claimed`); before
// that it has nothing to lay on.
function GraphTraceRows({ traces, claimed, onOpen }) {
    if (!traces.length) return null;
    const shown = traces.slice(0, 8);
    return (
        <div className="mt-[10px] border-t border-[color:var(--line-soft)] pt-[8px]" data-graph-traces>
            <div className="mb-[4px] flex items-baseline gap-[8px]">
                <span className="min-w-0 flex-1 text-[12px] text-[color:var(--muted)] first-letter:uppercase">traces in this graph</span>
                {claimed ? (
                    <button type="button" data-graph-open-canvas="" onClick={() => onOpen(null)} className={LINK}>
                        open in investigation
                    </button>
                ) : (
                    <span className="shrink-0 text-[10.5px] text-[color:var(--muted-soft)]" data-graph-open-unclaimed="">nothing claimed yet</span>
                )}
            </div>
            {shown.map((t) => {
                const counts = [
                    t.label,
                    t.claims ? `${t.claims} claim${t.claims === 1 ? "" : "s"}` : null,
                    `${t.anchors} anchor${t.anchors === 1 ? "" : "s"}`,
                ].filter(Boolean).join(" \u00b7 ");
                return (
                    <div key={t.key} className={`${ROW} items-baseline`}>
                        <span className="shrink-0 tabular-nums text-[color:var(--ink)]">{t.id.slice(0, 8)}</span>
                        <OverflowTooltip as="span" text={counts} className="min-w-0 flex-1 text-[color:var(--muted)]">{counts}</OverflowTooltip>
                    </div>
                );
            })}
            {traces.length > shown.length ? <div className="text-[10px] text-[color:var(--muted-soft)]">+{traces.length - shown.length} more</div> : null}
        </div>
    );
}

export function graphPillText(summary) {
    if (!summary || (!summary.nodes && !summary.claims?.total)) return "empty";
    const claims = summary.claims?.total ? `, ${summary.claims.supported}/${summary.claims.total} claims` : "";
    return `${summary.nodes}${claims}`;
}

export function graphKindsLine(summary) {
    const kinds = Object.entries(summary?.byKind || {})
        .sort((a, b) => b[1] - a[1])
        .map(([kind, n]) => `${n} ${pluralKind(kind, n)}`)
        .join(", ");
    const claims = summary?.claims?.total
        ? `${kinds ? "; " : ""}claims: ${summary.claims.supported} supported, ${summary.claims.insufficient} insufficient`
        : "";
    return kinds + claims;
}

function GraphIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="6" cy="12" r="2" />
            <circle cx="18" cy="6" r="2" />
            <circle cx="18" cy="18" r="2" />
            <path d="M7.8 11 16.2 6.9M7.8 13 16.2 17.1" />
        </svg>
    );
}

export function scrollToLatestGraphBlock() {
    const blocks = document.querySelectorAll("[data-graph-block]");
    const last = blocks[blocks.length - 1];
    if (last && typeof last.scrollIntoView === "function") last.scrollIntoView({ block: "center", behavior: "smooth" });
    return Boolean(last);
}

// `cell`: drawn as a cell of the composer's band in a framed pane (divided by
// a hairline, no box of its own) rather than a boxed button beside the input.
export default function InvestigationGraphPill({ chatId, chatTitle = "", cell = false }) {
    const key = graphKeyFor(chatId);
    const version = useSyncExternalStore((cb) => subscribeGraph(key, cb), () => graphVersion(key), () => 0);
    // The registry hands back the same graph object across versions; the
    // version is what tells the memos the object's contents moved.
    const graph = useMemo(() => readLiveGraph(key), [key, version]);
    // Right after a reload the browser store may not be readable yet (the
    // identity behind its key resolves asynchronously); re-read a few times
    // while the graph still looks empty.
    const retries = useRef(0);
    useEffect(() => {
        if (graph.nodes.size || graph.claims.length || retries.current >= 3) return undefined;
        const t = setTimeout(() => { retries.current += 1; notifyGraphChanged(key); }, 1500);
        return () => clearTimeout(t);
    }, [key, version, graph]);
    useEffect(() => { retries.current = 0; }, [key]);
    const summary = useMemo(() => graphSummary(graph), [graph, version]);
    // Each claim's door into the one trace it is about, read off the full
    // graph's evidence.
    const canOpen = !!parseGraphFocus(key);
    const claimTrace = useMemo(() => {
        const byId = new Map(graph.claims.map((c) => [c.id, c]));
        const found = new Map();
        return (id) => {
            if (!found.has(id)) found.set(id, canOpen && byId.has(id) ? claimTraceSession(graph, byId.get(id)) : null);
            return found.get(id);
        };
    }, [graph, version, canOpen]);
    const traces = useMemo(() => (canOpen ? graphTraceSessions(graph) : []), [graph, version, canOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    const claimed = useMemo(() => canOpen && workspaceTraces(graph).length > 0, [graph, version, canOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    // The investigation's canvas, or that canvas with one claim's trace in view.
    const openCanvas = (traceId = null) => {
        if (openGraphCanvas({ graph, graphKey: key, title: chatTitle, focus: traceId })) setOpen(false);
    };
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    // The panel is portalled to the body and fixed above the button, so the
    // transcript's stacking and the composer's translucent surfaces cannot
    // show through it. It sits above every console layer, the floating
    // overlay's scrim included, and is bounded to the room above the button
    // so a long claims list scrolls inside it rather than past the top.
    const [anchor, setAnchor] = useState(null);
    useEffect(() => {
        if (!open) return undefined;
        const place = () => {
            const r = wrapRef.current?.getBoundingClientRect();
            // Held on screen: beside a narrow column the button sits closer
            // to the window's left than the panel is wide, and the panel
            // slides right rather than running off the edge.
            const width = Math.min(640, window.innerWidth * 0.8, window.innerWidth - 16);
            if (r) setAnchor({ bottom: Math.max(8, window.innerHeight - r.top + 6), right: Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - 8 - width)), width, maxHeight: Math.max(160, r.top - 14) });
        };
        place();
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        const onDown = (e) => {
            const inButton = wrapRef.current?.contains(e.target);
            const inPanel = panelRef.current?.contains(e.target);
            if (!inButton && !inPanel) setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        window.addEventListener("resize", place);
        return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); window.removeEventListener("resize", place); };
    }, [open]);
    const empty = !summary.nodes && !summary.claims.total;
    const label = empty ? "Knowledge graph: empty" : `Knowledge graph: ${summary.nodes} anchor${summary.nodes === 1 ? "" : "s"}${summary.claims.total ? `, ${summary.claims.supported}/${summary.claims.total} claims` : ""}`;
    return (
        <span ref={wrapRef} className={`relative inline-flex shrink-0${cell ? " self-stretch" : ""}`}>
            <HoverTooltip label={label} placement="top">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={label}
                    aria-pressed={open}
                    className={[
                        "grid shrink-0 place-items-center transition-colors cursor-pointer [&_svg]:block [&_svg]:h-[14px] [&_svg]:w-[14px]",
                        cell
                            ? `w-[38px] border-l border-[color:var(--line-soft)] ${open ? "text-[color:var(--ink)] [background-image:linear-gradient(color-mix(in_srgb,var(--ink)_10%,transparent),color-mix(in_srgb,var(--ink)_10%,transparent))]" : ""}`
                            : `h-[34px] w-[34px] rounded-[7px] border bg-[color:var(--control-bg)] ${open ? "border-[color:var(--line-strong)] text-[color:var(--ink)]" : "border-[color:var(--line)]"}`,
                        empty && !open ? "text-[color:var(--muted-soft)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                    ].join(" ")}
                >
                    <GraphIcon />
                </button>
            </HoverTooltip>
            {open && anchor ? createPortal(
                // Framed as the workspace's floating panels are (the board's
                // peeks): a rounded hairline frame with the node shadow, its
                // head a band of hairline cells as the dock's tab row is.
                <div ref={panelRef} role="dialog" aria-label="Knowledge graph" style={{ bottom: anchor.bottom, right: anchor.right, width: anchor.width, maxHeight: anchor.maxHeight }} className="fixed z-[10002] overflow-y-auto rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] shadow-[var(--node-shadow-lift)]">
                    <div className="sticky top-0 z-[2] flex h-[30px] items-stretch border-b border-[color:var(--line-soft)] bg-[color:var(--bg)]">
                        <span className="flex min-w-0 flex-1 items-center px-[12px] text-[12px] text-[color:var(--muted)]">Knowledge graph</span>
                        {empty ? null : (
                            <button type="button" onClick={() => { scrollToLatestGraphBlock(); setOpen(false); }} className="shrink-0 cursor-pointer border-l border-[color:var(--line-soft)] bg-transparent px-[10px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                                Latest in transcript
                            </button>
                        )}
                        <HoverTooltip label="Close (Esc)" placement="top">
                            <button type="button" onClick={() => setOpen(false)} aria-label="Close graph" className="grid w-[30px] shrink-0 cursor-pointer place-items-center border-l border-[color:var(--line-soft)] bg-transparent text-[13px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                                {"\u00d7"}
                            </button>
                        </HoverTooltip>
                    </div>
                    <div className="px-[12px] pb-[12px] pt-[8px]">
                        <div className="mb-[8px] text-[11px] text-[color:var(--muted)]">{empty ? "Nothing has been queried in this chat yet." : graphKindsLine(summary)}</div>
                        {empty ? null : <GraphTimeline graph={graph} version={version} claimTrace={claimTrace} onOpenTrace={(traceId) => openCanvas(traceId)} />}
                        <GraphTraceRows traces={traces} claimed={claimed} onOpen={() => openCanvas()} />
                    </div>
                </div>,
                document.body,
            ) : null}
        </span>
    );
}
